import React, { useState, useEffect } from 'react';
import { Users, Calendar, CheckCircle, XCircle, Clock, BarChart3, Filter, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';

interface Meeting {
  id: string;
  title: string;
  date: string;
  confirmed: boolean | null;
  meeting_type: string;
  created_at: string;
  created_by: string;
}

interface User {
  id: string;
  email: string;
}

interface DashboardStats {
  totalMeetings: number;
  confirmedMeetings: number;
  pendingMeetings: number;
  rejectedMeetings: number;
  upcomingMeetings: number;
  meetingsPerType: {
    [key: string]: number;
  };
  meetingsPerMonth: {
    [key: string]: number;
  };
  meetingsPerUser: {
    [key: string]: number;
  };
}

export default function Analytics() {
  const [stats, setStats] = useState<DashboardStats>({
    totalMeetings: 0,
    confirmedMeetings: 0,
    pendingMeetings: 0,
    rejectedMeetings: 0,
    upcomingMeetings: 0,
    meetingsPerType: {},
    meetingsPerMonth: {},
    meetingsPerUser: {},
  });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  
  // Filter states
  const [dateRange, setDateRange] = useState({
    start: '',
    end: '',
  });
  const [statusFilter, setStatusFilter] = useState<'all' | 'confirmed' | 'pending' | 'rejected'>('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [userFilter, setUserFilter] = useState('all');

  const meetingTypes = {
    phone_interview: 'Telefoninterview mit TOK Rec2Rec',
    briefing: 'Rücksprache/ Briefing mit TOK Rec2Rec',
    p1: 'P1 - Vorstellungsgespräch',
    p2: 'P2 - Vorstellungsgespräch',
    p3: 'P3 - Vorstellungsgespräch',
    p4: 'P4 - Vorstellungsgespräch',
    p5: 'P5 - Vorstellungsgespräch',
    contract: 'Vertragsgespräch',
    other: 'sonstiges'
  };

  useEffect(() => {
    document.title = 'Analytics | TOK rec2rec Terminplaner';
    fetchUsers();
    fetchStats();
  }, [dateRange, statusFilter, typeFilter, userFilter]);

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email')
        .order('email');

      if (error) throw error;
      setUsers(data || []);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Fehler beim Laden der Benutzer');
    }
  };

  const fetchStats = async () => {
    try {
      let query = supabase
        .from('meetings')
        .select(`
          *,
          user_profiles!meetings_created_by_fkey (
            email
          )
        `);

      // Apply date range filter
      if (dateRange.start) {
        query = query.gte('date', dateRange.start);
      }
      if (dateRange.end) {
        query = query.lte('date', dateRange.end);
      }

      // Apply status filter
      if (statusFilter === 'confirmed') {
        query = query.eq('confirmed', true);
      } else if (statusFilter === 'pending') {
        query = query.is('confirmed', null);
      } else if (statusFilter === 'rejected') {
        query = query.eq('confirmed', false);
      }

      // Apply type filter
      if (typeFilter !== 'all') {
        query = query.eq('meeting_type', typeFilter);
      }

      // Apply user filter
      if (userFilter !== 'all') {
        query = query.eq('created_by', userFilter);
      }

      const { data: meetings, error } = await query;

      if (error) throw error;

      const now = new Date();
      const stats: DashboardStats = {
        totalMeetings: meetings.length,
        confirmedMeetings: meetings.filter(m => m.confirmed === true).length,
        pendingMeetings: meetings.filter(m => m.confirmed === null).length,
        rejectedMeetings: meetings.filter(m => m.confirmed === false).length,
        upcomingMeetings: meetings.filter(m => new Date(m.date) > now).length,
        meetingsPerType: {},
        meetingsPerMonth: {},
        meetingsPerUser: {},
      };

      // Calculate meetings per type
      meetings.forEach(meeting => {
        const type = meetingTypes[meeting.meeting_type as keyof typeof meetingTypes] || meeting.meeting_type;
        stats.meetingsPerType[type] = (stats.meetingsPerType[type] || 0) + 1;
      });

      // Calculate meetings per month
      meetings.forEach(meeting => {
        const date = new Date(meeting.date);
        const monthYear = date.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
        stats.meetingsPerMonth[monthYear] = (stats.meetingsPerMonth[monthYear] || 0) + 1;
      });

      // Calculate meetings per user
      meetings.forEach(meeting => {
        const userEmail = meeting.user_profiles?.email || 'Unbekannt';
        stats.meetingsPerUser[userEmail] = (stats.meetingsPerUser[userEmail] || 0) + 1;
      });

      setStats(stats);
    } catch (error) {
      console.error('Error fetching stats:', error);
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setLoading(false);
    }
  };

  const exportData = async () => {
    try {
      const { data: meetings, error } = await supabase
        .from('meetings')
        .select(`
          *,
          user_profiles!meetings_created_by_fkey (
            email
          )
        `)
        .order('date', { ascending: false });

      if (error) throw error;

      const csvContent = [
        // CSV Header
        ['Datum', 'Titel', 'Vorname', 'Nachname', 'Status', 'Art', 'Ort', 'Dauer', 'Erstellt von', 'Erstellt am'].join(','),
        // CSV Data
        ...meetings.map(meeting => [
          new Date(meeting.date).toLocaleString('de-DE'),
          `"${meeting.title}"`,
          `"${meeting.first_name}"`,
          `"${meeting.last_name}"`,
          meeting.confirmed === true ? 'Bestätigt' : meeting.confirmed === false ? 'Abgelehnt' : 'Ausstehend',
          `"${meetingTypes[meeting.meeting_type as keyof typeof meetingTypes] || meeting.meeting_type}"`,
          `"${meeting.location || ''}"`,
          `${meeting.duration} min`,
          `"${meeting.user_profiles?.email || ''}"`,
          new Date(meeting.created_at).toLocaleString('de-DE'),
        ].join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `termine_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      
      toast.success('Daten erfolgreich exportiert');
    } catch (error) {
      console.error('Error exporting data:', error);
      toast.error('Fehler beim Exportieren der Daten');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-48">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500" />
      </div>
    );
  }

  return (
    <div className="px-4 py-6 sm:px-0 space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h2>
        <div className="flex space-x-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <Filter className="h-5 w-5 mr-2" />
            Filter
          </button>
          <button
            onClick={exportData}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <Download className="h-5 w-5 mr-2" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white p-4 rounded-lg shadow border-2 border-gray-200">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Zeitraum</label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as any)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
              >
                <option value="all">Alle</option>
                <option value="confirmed">Bestätigt</option>
                <option value="pending">Ausstehend</option>
                <option value="rejected">Abgelehnt</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Termin-Art</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
              >
                <option value="all">Alle</option>
                {Object.entries(meetingTypes).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Benutzer</label>
              <select
                value={userFilter}
                onChange={(e) => setUserFilter(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500"
              >
                <option value="all">Alle Benutzer</option>
                {users.map(user => (
                  <option key={user.id} value={user.id}>
                    {user.email}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg shadow border-2 border-gray-200">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-blue-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Gesamt</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalMeetings}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-2 border-gray-200">
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Bestätigt</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.confirmedMeetings}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-2 border-gray-200">
          <div className="flex items-center">
            <Clock className="h-8 w-8 text-yellow-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Ausstehend</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.pendingMeetings}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-2 border-gray-200">
          <div className="flex items-center">
            <XCircle className="h-8 w-8 text-red-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Abgelehnt</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.rejectedMeetings}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow border-2 border-gray-200">
          <div className="flex items-center">
            <Calendar className="h-8 w-8 text-purple-500" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Anstehend</p>
              <p className="text-2xl font-semibold text-gray-900">{stats.upcomingMeetings}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Meetings per Type */}
        <div className="bg-white p-6 rounded-lg shadow border-2 border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-blue-500" />
            Termine nach Art
          </h3>
          <div className="space-y-4">
            {Object.entries(stats.meetingsPerType).map(([type, count]) => (
              <div key={type} className="flex items-center">
                <div className="flex-1">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-900">{type}</span>
                    <span className="ml-2 text-sm text-gray-500">
                      ({Math.round((count / stats.totalMeetings) * 100)}%)
                    </span>
                  </div>
                  <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-500 h-2 rounded-full"
                      style={{ width: `${(count / stats.totalMeetings) * 100}%` }}
                    />
                  </div>
                </div>
                <span className="ml-4 text-sm font-medium text-gray-900">{count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Meetings per Month */}
        <div className="bg-white p-6 rounded-lg shadow border-2 border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <BarChart3 className="w-5 h-5 mr-2 text-green-500" />
            Termine pro Monat
          </h3>
          <div className="space-y-4">
            {Object.entries(stats.meetingsPerMonth)
              .sort((a, b) => {
                const dateA = new Date(a[0].split(' ')[1], new Date(Date.parse(a[0])).getMonth());
                const dateB = new Date(b[0].split(' ')[1], new Date(Date.parse(b[0])).getMonth());
                return dateB.getTime() - dateA.getTime();
              })
              .map(([month, count]) => (
                <div key={month} className="flex items-center">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">{month}</span>
                      <span className="ml-2 text-sm text-gray-500">
                        ({Math.round((count / stats.totalMeetings) * 100)}%)
                      </span>
                    </div>
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-green-500 h-2 rounded-full"
                        style={{ width: `${(count / stats.totalMeetings) * 100}%` }}
                      />
                    </div>
                  </div>
                  <span className="ml-4 text-sm font-medium text-gray-900">{count}</span>
                </div>
              ))}
          </div>
        </div>

        {/* Meetings per User */}
        <div className="bg-white p-6 rounded-lg shadow border-2 border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
            <Users className="w-5 h-5 mr-2 text-purple-500" />
            Termine pro Benutzer
          </h3>
          <div className="space-y-4">
            {Object.entries(stats.meetingsPerUser)
              .sort((a, b) => b[1] - a[1])
              .map(([email, count]) => (
                <div key={email} className="flex items-center">
                  <div className="flex-1">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">{email}</span>
                      <span className="ml-2 text-sm text-gray-500">
                        ({Math.round((count / stats.totalMeetings) * 100)}%)
                      </span>
                    </div>
                    <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-purple-500 h-2 rounded-full"
                        style={{ width: `${(count / stats.totalMeetings) * 100}%` }}
                      />
                    </div>
                  </div>
                  <span className="ml-4 text-sm font-medium text-gray-900">{count}</span>
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}